/**
 * AnalyzerInstagramTab — Aba Instagram da area Digital do CROSS M3.
 *
 * Implementa o "Analyzer Instagram (Nivel B)": consome os endpoints
 * GET /analyzer/profile?post_limit=N e POST /analyzer/report e renderiza:
 *
 *   1. Seletor de post_limit (10/25/50/100) — controla a profundidade da coleta.
 *   2. Perfil: foto/username/nome + KPIs do perfil (seguidores, engajamento,
 *      medias, frequencia, melhor dia).
 *   3. Hashtags top, distribuicao de conteudo e desempenho por formato
 *      (formatPerformance + bestFormat destacado).
 *   4. Posts recentes (recentPosts do profile — /analyzer/posts nao eh chamado
 *      separadamente para evitar dupla coleta; os posts ja vem no profile).
 *   5. Parecer da IA: botao "Gerar parecer da CROSS" que chama POST /analyzer/report.
 *      Loading ~40s com progresso claro; rate-limit com mensagem amigavel.
 *   6. MetricNotice para alcance por video descontinuado (Meta jun/2026).
 *
 * Estados honestos:
 *   - 404 no_instagram_source → instrui o usuario a conectar em /sources
 *   - 409 source_not_connected → instrui a reconectar
 *   - 502 collect_failed / report_failed → erro com retry
 *   - insightsAvailable=false → nota honesta sobre permissao
 *   - alcance por video = 0 (Meta descontinuou jun/2026) → MetricNotice
 *   - bestFormat null → "amostra insuficiente"
 *
 * Acessibilidade: AA — contrast, focus, aria, keyboard nav.
 * i18n: namespace `analyzer.*` e `metricNotices.*` em i18n.ts.
 * Zero string hardcoded fora de i18n.
 * Zero emoji.
 *
 * F3 — 2026-06-09
 */

import '../styles/screen-analyzer.css';
import '../styles/screen-digital.css';
import { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { t } from '../lib/i18n';
import { apiClient, ApiError } from '../lib/api-client';
import { SkeletonGrid, Skeleton } from '../components/Skeleton';
import { MetricNotice } from '../components/MetricNotice';
import { DigitalPeriodBanner } from '../components/DigitalPeriodBanner';

// ---------------------------------------------------------------------------
// Constante: opcoes validas de post_limit
// ---------------------------------------------------------------------------

const POST_LIMIT_OPTIONS = [10, 25, 50, 100] as const;
type PostLimit = (typeof POST_LIMIT_OPTIONS)[number];
const DEFAULT_POST_LIMIT: PostLimit = 25;

// ---------------------------------------------------------------------------
// Tipos do contrato backend (espelham src/apps/api/src/analyzer/types.ts)
// ---------------------------------------------------------------------------

interface AnalyzedPost {
  readonly id: string;
  readonly mediaType: string;
  readonly caption: string;
  readonly timestamp: string;
  readonly likeCount: number;
  readonly commentsCount: number;
  readonly reach?: number;
  readonly saves?: number;
  readonly shares?: number;
  readonly permalink: string;
  readonly engagementRate: number;
}

interface FormatPerformance {
  readonly format: string;
  readonly count: number;
  readonly avgEngagementRate: number;
  readonly avgReach: number;
  readonly avgSaves: number;
  readonly avgShares: number;
  readonly avgLikes: number;
  readonly avgComments: number;
}

interface ProfileAnalysis {
  readonly igUserId: string;
  readonly username: string;
  readonly name: string;
  readonly biography: string;
  readonly profilePicUrl: string;
  readonly website: string;
  readonly followersCount: number;
  readonly followingCount: number;
  readonly mediaCount: number;
  readonly engagementRate: number;
  readonly avgLikes: number;
  readonly avgComments: number;
  readonly avgReach: number;
  readonly avgSaves: number;
  readonly avgShares: number;
  readonly postingFrequency: number;
  readonly bestPostingDay: string;
  readonly topHashtags: ReadonlyArray<{ readonly tag: string; readonly count: number }>;
  readonly contentDistribution: Readonly<Record<string, number>>;
  readonly formatPerformance: readonly FormatPerformance[];
  readonly bestFormat: string | null;
  readonly recentPosts: readonly AnalyzedPost[];
  readonly collectedAt: string;
  readonly insightsAvailable: boolean;
}

interface AnalyzerReport {
  readonly tenantId: string;
  readonly username: string;
  readonly score: number;
  readonly summary: string;
  readonly strengths: readonly string[];
  readonly weaknesses: readonly string[];
  readonly recommendations: readonly string[];
  readonly contentStrategy: string;
  readonly generatedAt: string;
}

// ---------------------------------------------------------------------------
// Load states
// ---------------------------------------------------------------------------

type ProfileState =
  | { kind: 'loading' }
  | { kind: 'no_source' }
  | { kind: 'not_connected' }
  | { kind: 'error' }
  | { kind: 'ready'; profile: ProfileAnalysis };

type ReportState =
  | { kind: 'idle' }
  | { kind: 'generating' }
  | { kind: 'ready'; report: AnalyzerReport }
  | { kind: 'rate_limited' }
  | { kind: 'error' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(2)}%`;
}

function formatNumber(n: number): string {
  return new Intl.NumberFormat('pt-BR').format(Math.round(n));
}

function humanizeFormatName(raw: string): string {
  const map: Record<string, string> = {
    IMAGE: t('analyzer.formatImage'),
    VIDEO: t('analyzer.formatVideo'),
    CAROUSEL_ALBUM: t('analyzer.formatCarousel'),
    REEL: t('analyzer.formatReel'),
  };
  return map[raw.toUpperCase()] ?? t('analyzer.formatOther');
}

// ---------------------------------------------------------------------------
// Hook: carrega o perfil do analyzer
// ---------------------------------------------------------------------------

function useAnalyzerProfile(postLimit: PostLimit) {
  const [state, setState] = useState<ProfileState>({ kind: 'loading' });
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setState({ kind: 'loading' });

    const load = async (): Promise<void> => {
      try {
        const data = await apiClient.get<{ profile: ProfileAnalysis }>(
          `/analyzer/profile?post_limit=${postLimit}`,
        );
        if (cancelled) return;
        setState({ kind: 'ready', profile: data.profile });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError) {
          if (err.status === 404 && err.code === 'no_instagram_source') {
            setState({ kind: 'no_source' });
            return;
          }
          if (err.status === 409 && err.code === 'source_not_connected') {
            setState({ kind: 'not_connected' });
            return;
          }
        }
        setState({ kind: 'error' });
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [retryKey, postLimit]);

  const retry = useCallback(() => setRetryKey((k) => k + 1), []);
  return { state, retry };
}

// ---------------------------------------------------------------------------
// Hook: gera o parecer da IA
// ---------------------------------------------------------------------------

function useAnalyzerReport(postLimit: PostLimit) {
  const [state, setState] = useState<ReportState>({ kind: 'idle' });

  const generate = useCallback(async (): Promise<void> => {
    setState({ kind: 'generating' });
    try {
      const data = await apiClient.post<{ report: AnalyzerReport; profile: ProfileAnalysis }>(
        '/analyzer/report',
        { post_limit: postLimit },
      );
      setState({ kind: 'ready', report: data.report });
    } catch (err) {
      if (err instanceof ApiError) {
        if (err.status === 429) {
          setState({ kind: 'rate_limited' });
          return;
        }
      }
      setState({ kind: 'error' });
    }
  }, [postLimit]);

  const reset = useCallback(() => setState({ kind: 'idle' }), []);

  return { state, generate, reset };
}

// ---------------------------------------------------------------------------
// Sub-componente: perfil header
// ---------------------------------------------------------------------------

interface ProfileHeaderProps {
  readonly profile: ProfileAnalysis;
}

function ProfileHeader({ profile }: ProfileHeaderProps): JSX.Element {
  const collectedDateStr = t('analyzer.profileCollectedAt', {
    date: formatDate(profile.collectedAt),
  });

  return (
    <div className="az-profile-header" aria-label={`Perfil @${profile.username}`}>
      {profile.profilePicUrl.length > 0 ? (
        <img
          className="az-profile-pic"
          src={profile.profilePicUrl}
          alt={`Foto de perfil de @${profile.username}`}
          width={64}
          height={64}
          loading="lazy"
        />
      ) : (
        <span className="az-profile-pic-placeholder material-symbols-rounded" aria-hidden="true">
          account_circle
        </span>
      )}
      <div className="az-profile-info">
        <p className="az-profile-username">@{profile.username}</p>
        {profile.name.length > 0 ? <p className="az-profile-name">{profile.name}</p> : null}
        <p className="az-profile-collected" aria-label={collectedDateStr}>
          <span className="material-symbols-rounded az-profile-clock-icon" aria-hidden="true">
            schedule
          </span>
          {collectedDateStr}
        </p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: grade de KPIs do perfil
// ---------------------------------------------------------------------------

interface ProfileKpiGridProps {
  readonly profile: ProfileAnalysis;
}

function ProfileKpiGrid({ profile }: ProfileKpiGridProps): JSX.Element {
  const kpis: ReadonlyArray<{
    readonly label: string;
    readonly value: string;
    readonly icon: string;
  }> = [
    {
      label: t('analyzer.profileFollowers'),
      value: formatNumber(profile.followersCount),
      icon: 'group',
    },
    {
      label: t('analyzer.profileFollowing'),
      value: formatNumber(profile.followingCount),
      icon: 'person_add',
    },
    {
      label: t('analyzer.profileMediaCount'),
      value: formatNumber(profile.mediaCount),
      icon: 'grid_on',
    },
    {
      label: t('analyzer.profileEngagement'),
      value: formatPercent(profile.engagementRate),
      icon: 'favorite',
    },
    {
      label: t('analyzer.profileAvgLikes'),
      value: formatNumber(profile.avgLikes),
      icon: 'thumb_up',
    },
    {
      label: t('analyzer.profileAvgComments'),
      value: formatNumber(profile.avgComments),
      icon: 'chat_bubble',
    },
    {
      label: t('analyzer.profileAvgReach'),
      value: profile.insightsAvailable ? formatNumber(profile.avgReach) : '—',
      icon: 'visibility',
    },
    {
      label: t('analyzer.profileAvgSaves'),
      value: profile.insightsAvailable ? formatNumber(profile.avgSaves) : '—',
      icon: 'bookmark',
    },
    {
      label: t('analyzer.profileFrequency'),
      value: profile.postingFrequency.toFixed(1),
      icon: 'calendar_today',
    },
    {
      label: t('analyzer.profileBestDay'),
      value: profile.bestPostingDay,
      icon: 'star',
    },
  ];

  return (
    <div className="az-kpi-grid" role="list" aria-label={t('analyzer.profileSection')}>
      {kpis.map((kpi) => (
        <article
          key={kpi.label}
          className="az-kpi-card"
          role="listitem"
          aria-label={`${kpi.label}: ${kpi.value}`}
        >
          <span className="material-symbols-rounded az-kpi-icon" aria-hidden="true">
            {kpi.icon}
          </span>
          <p className="az-kpi-label">{kpi.label}</p>
          <p className="az-kpi-value">{kpi.value}</p>
        </article>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: hashtags top
// ---------------------------------------------------------------------------

interface TopHashtagsProps {
  readonly tags: ReadonlyArray<{ readonly tag: string; readonly count: number }>;
}

function TopHashtags({ tags }: TopHashtagsProps): JSX.Element | null {
  if (tags.length === 0) return null;

  const maxCount = tags[0]?.count ?? 1;

  return (
    <section className="az-section" aria-labelledby="az-hashtags-heading">
      <h3 id="az-hashtags-heading" className="az-section-title">
        {t('analyzer.hashtagsSection')}
      </h3>
      <ul className="az-hashtag-list" aria-label={t('analyzer.hashtagsSection')}>
        {tags.slice(0, 10).map(({ tag, count }) => {
          const pct = maxCount > 0 ? Math.round((count / maxCount) * 100) : 0;
          const usedLabel =
            count === 1
              ? t('analyzer.hashtagUsed', { count: String(count) })
              : t('analyzer.hashtagUsedPlural', { count: String(count) });
          return (
            <li key={tag} className="az-hashtag-item" aria-label={`${tag} — ${usedLabel}`}>
              <span className="az-hashtag-tag">#{tag}</span>
              <span className="az-hashtag-bar-wrap" aria-hidden="true">
                <span className="az-hashtag-bar-fill" style={{ width: `${pct}%` }} />
              </span>
              <span className="az-hashtag-count">{count}</span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: distribuicao de conteudo
// ---------------------------------------------------------------------------

interface ContentDistProps {
  readonly dist: Readonly<Record<string, number>>;
}

function ContentDistribution({ dist }: ContentDistProps): JSX.Element | null {
  const entries = Object.entries(dist).sort(([, a], [, b]) => b - a);
  if (entries.length === 0) return null;

  const total = entries.reduce((s, [, n]) => s + n, 0);

  return (
    <section className="az-section" aria-labelledby="az-content-dist-heading">
      <h3 id="az-content-dist-heading" className="az-section-title">
        {t('analyzer.contentDistSection')}
      </h3>
      <ul className="az-dist-list" aria-label={t('analyzer.contentDistSection')}>
        {entries.map(([format, count]) => {
          const pct = total > 0 ? ((count / total) * 100).toFixed(0) : '0';
          const label = humanizeFormatName(format);
          const unitLabel =
            count === 1 ? t('analyzer.contentDistUnit') : t('analyzer.contentDistUnitPlural');
          return (
            <li
              key={format}
              className="az-dist-item"
              aria-label={`${label}: ${count} ${unitLabel} (${pct}%)`}
            >
              <span className="az-dist-label">{label}</span>
              <span className="az-dist-bar-wrap" aria-hidden="true">
                <span className="az-dist-bar-fill" style={{ width: `${pct}%` }} />
              </span>
              <span className="az-dist-meta">
                {count} {unitLabel} <span className="az-dist-pct">{pct}%</span>
              </span>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: desempenho por formato
// ---------------------------------------------------------------------------

interface FormatPerformanceTableProps {
  readonly formats: readonly FormatPerformance[];
  readonly bestFormat: string | null;
  readonly insightsAvailable: boolean;
}

function FormatPerformanceTable({
  formats,
  bestFormat,
  insightsAvailable,
}: FormatPerformanceTableProps): JSX.Element | null {
  if (formats.length === 0) return null;

  return (
    <section className="az-section" aria-labelledby="az-format-perf-heading">
      <div className="az-section-header-row">
        <h3 id="az-format-perf-heading" className="az-section-title">
          {t('analyzer.formatPerfSection')}
        </h3>
        {bestFormat !== null ? (
          <span
            className="az-best-format-badge"
            aria-label={`${t('analyzer.formatPerfBestLabel')}: ${humanizeFormatName(bestFormat)}`}
          >
            <span className="material-symbols-rounded az-best-format-icon" aria-hidden="true">
              star
            </span>
            {t('analyzer.formatPerfBest')}: {humanizeFormatName(bestFormat)}
          </span>
        ) : (
          <span className="az-best-format-null" aria-label={t('analyzer.formatPerfBestNull')}>
            {t('analyzer.formatPerfBestNull')}
          </span>
        )}
      </div>

      {!insightsAvailable ? (
        <div className="az-note az-note--info" role="note">
          <span className="material-symbols-rounded az-note-icon" aria-hidden="true">
            info
          </span>
          {t('analyzer.insightsUnavailableNote')}
        </div>
      ) : null}

      <div
        className="az-format-table-wrap"
        role="region"
        aria-label={t('analyzer.formatPerfSection')}
      >
        <table className="az-format-table">
          <thead>
            <tr>
              <th scope="col">{t('analyzer.formatPerfFormatCol')}</th>
              <th scope="col" className="az-format-th-num">
                {t('analyzer.formatPerfCountCol')}
              </th>
              <th scope="col" className="az-format-th-num">
                {t('analyzer.formatPerfEngagementCol')}
              </th>
              <th scope="col" className="az-format-th-num">
                {t('analyzer.formatPerfLikesCol')}
              </th>
              <th scope="col" className="az-format-th-num">
                {t('analyzer.formatPerfReachCol')}
              </th>
              <th scope="col" className="az-format-th-num">
                {t('analyzer.formatPerfSavesCol')}
              </th>
            </tr>
          </thead>
          <tbody>
            {formats.map((fp) => {
              const isBest = bestFormat !== null && fp.format === bestFormat;
              const label = humanizeFormatName(fp.format);
              const isVideoType =
                fp.format.toUpperCase() === 'VIDEO' || fp.format.toUpperCase() === 'REEL';
              return (
                <tr
                  key={fp.format}
                  className={isBest ? 'az-format-row az-format-row--best' : 'az-format-row'}
                  aria-label={`${label}${isBest ? ` — ${t('analyzer.formatPerfBestLabel')}` : ''}`}
                >
                  <td className="az-format-name">
                    {isBest ? (
                      <span className="material-symbols-rounded az-format-star" aria-hidden="true">
                        star
                      </span>
                    ) : null}
                    {label}
                  </td>
                  <td className="az-format-num">{fp.count}</td>
                  <td className="az-format-num">{formatPercent(fp.avgEngagementRate)}</td>
                  <td className="az-format-num">{formatNumber(fp.avgLikes)}</td>
                  <td className="az-format-num">
                    {insightsAvailable && !isVideoType ? formatNumber(fp.avgReach) : '—'}
                  </td>
                  <td className="az-format-num">
                    {insightsAvailable ? formatNumber(fp.avgSaves) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* MetricNotice para alcance por video descontinuado (Meta jun/2026) */}
      {formats.some(
        (f) => f.format.toUpperCase() === 'VIDEO' || f.format.toUpperCase() === 'REEL',
      ) ? (
        <MetricNotice id="instagram.video_reach" />
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: posts recentes
// ---------------------------------------------------------------------------

interface RecentPostsProps {
  readonly posts: readonly AnalyzedPost[];
  readonly insightsAvailable: boolean;
}

function RecentPosts({ posts, insightsAvailable }: RecentPostsProps): JSX.Element | null {
  if (posts.length === 0) return null;

  return (
    <section className="az-section" aria-labelledby="az-posts-heading">
      <h3 id="az-posts-heading" className="az-section-title">
        {t('analyzer.postsSection')}
      </h3>
      <ul className="az-posts-list" aria-label={t('analyzer.postsSection')}>
        {posts.map((post) => {
          const caption =
            post.caption.length > 0
              ? post.caption.slice(0, 120) + (post.caption.length > 120 ? '…' : '')
              : t('analyzer.postNoCaption');

          const postDate = (() => {
            try {
              return new Intl.DateTimeFormat('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              }).format(new Date(post.timestamp));
            } catch {
              return post.timestamp;
            }
          })();

          const isVideoPost =
            post.mediaType.toUpperCase() === 'VIDEO' || post.mediaType.toUpperCase() === 'REEL';

          // Alcance por video: exibe "—" e MetricNotice quando VIDEO/REEL
          const reachDisplay =
            insightsAvailable && !isVideoPost ? formatNumber(post.reach ?? 0) : '—';

          return (
            <li key={post.id} className="az-post-card" aria-label={`Post de ${postDate}`}>
              <div className="az-post-meta-row">
                <span className="az-post-type">{humanizeFormatName(post.mediaType)}</span>
                <time className="az-post-date" dateTime={post.timestamp}>
                  {postDate}
                </time>
              </div>
              <p className="az-post-caption">{caption}</p>
              <div className="az-post-kpis" role="list">
                <span
                  className="az-post-kpi"
                  role="listitem"
                  aria-label={`${t('analyzer.postLikes')}: ${post.likeCount}`}
                >
                  <span className="material-symbols-rounded az-post-kpi-icon" aria-hidden="true">
                    thumb_up
                  </span>
                  {formatNumber(post.likeCount)}
                </span>
                <span
                  className="az-post-kpi"
                  role="listitem"
                  aria-label={`${t('analyzer.postComments')}: ${post.commentsCount}`}
                >
                  <span className="material-symbols-rounded az-post-kpi-icon" aria-hidden="true">
                    chat_bubble
                  </span>
                  {formatNumber(post.commentsCount)}
                </span>
                {insightsAvailable ? (
                  <>
                    <span
                      className="az-post-kpi"
                      role="listitem"
                      aria-label={`${t('analyzer.postReach')}: ${reachDisplay}`}
                    >
                      <span
                        className="material-symbols-rounded az-post-kpi-icon"
                        aria-hidden="true"
                      >
                        visibility
                      </span>
                      {reachDisplay}
                    </span>
                    <span
                      className="az-post-kpi"
                      role="listitem"
                      aria-label={`${t('analyzer.postSaves')}: ${post.saves ?? 0}`}
                    >
                      <span
                        className="material-symbols-rounded az-post-kpi-icon"
                        aria-hidden="true"
                      >
                        bookmark
                      </span>
                      {formatNumber(post.saves ?? 0)}
                    </span>
                  </>
                ) : null}
                <span
                  className="az-post-kpi az-post-kpi--engagement"
                  role="listitem"
                  aria-label={`${t('analyzer.postEngagement')}: ${formatPercent(post.engagementRate)}`}
                >
                  <span className="material-symbols-rounded az-post-kpi-icon" aria-hidden="true">
                    favorite
                  </span>
                  {formatPercent(post.engagementRate)}
                </span>
              </div>
              {/* MetricNotice para posts VIDEO/REEL com alcance desabilitado */}
              {isVideoPost ? <MetricNotice id="instagram.video_reach" /> : null}
              {post.permalink.length > 0 ? (
                <a
                  href={post.permalink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="az-post-link"
                  aria-label={`${t('analyzer.postViewOnInstagram')} — post de ${postDate}`}
                >
                  <span
                    className="material-symbols-rounded"
                    style={{ fontSize: 14 }}
                    aria-hidden="true"
                  >
                    open_in_new
                  </span>
                  {t('analyzer.postViewOnInstagram')}
                </a>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: Parecer da IA (score gauge + conteudo)
// ---------------------------------------------------------------------------

interface ScoreGaugeProps {
  readonly score: number;
}

function ScoreGauge({ score }: ScoreGaugeProps): JSX.Element {
  const clamped = Math.max(0, Math.min(100, score));

  // Cor: 0-39 vermelho, 40-69 amarelo, 70-100 verde
  const colorClass =
    clamped >= 70
      ? 'az-score-gauge--good'
      : clamped >= 40
        ? 'az-score-gauge--warn'
        : 'az-score-gauge--bad';

  return (
    <div
      className={`az-score-gauge ${colorClass}`}
      role="meter"
      aria-valuenow={clamped}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={`${t('analyzer.reportScore')}: ${clamped} ${t('analyzer.reportScoreLabel')}`}
    >
      <span className="az-score-number">{clamped}</span>
      <span className="az-score-label">{t('analyzer.reportScoreLabel')}</span>
    </div>
  );
}

interface ReportPanelProps {
  readonly report: AnalyzerReport;
}

function ReportPanel({ report }: ReportPanelProps): JSX.Element {
  return (
    <div className="az-report-panel" aria-labelledby="az-report-heading">
      <div className="az-report-header">
        <h3 id="az-report-heading" className="az-section-title">
          {t('analyzer.reportSection')}
        </h3>
        <time className="az-report-date" dateTime={report.generatedAt}>
          {t('analyzer.reportGeneratedAt', { date: formatDate(report.generatedAt) })}
        </time>
      </div>

      <div className="az-report-score-row">
        <ScoreGauge score={report.score} />
        <p className="az-report-summary">{report.summary}</p>
      </div>

      <div className="az-report-sections">
        {report.strengths.length > 0 ? (
          <div className="az-report-block" aria-labelledby="az-report-strengths">
            <h4
              id="az-report-strengths"
              className="az-report-block-title az-report-block-title--good"
            >
              <span className="material-symbols-rounded az-report-block-icon" aria-hidden="true">
                check_circle
              </span>
              {t('analyzer.reportStrengths')}
            </h4>
            <ul className="az-report-list">
              {report.strengths.map((s, i) => (
                <li key={i} className="az-report-list-item">
                  {s}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {report.weaknesses.length > 0 ? (
          <div className="az-report-block" aria-labelledby="az-report-weaknesses">
            <h4
              id="az-report-weaknesses"
              className="az-report-block-title az-report-block-title--warn"
            >
              <span className="material-symbols-rounded az-report-block-icon" aria-hidden="true">
                warning
              </span>
              {t('analyzer.reportWeaknesses')}
            </h4>
            <ul className="az-report-list">
              {report.weaknesses.map((w, i) => (
                <li key={i} className="az-report-list-item">
                  {w}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {report.recommendations.length > 0 ? (
          <div className="az-report-block" aria-labelledby="az-report-recs">
            <h4 id="az-report-recs" className="az-report-block-title">
              <span className="material-symbols-rounded az-report-block-icon" aria-hidden="true">
                lightbulb
              </span>
              {t('analyzer.reportRecommendations')}
            </h4>
            <ol className="az-report-list az-report-list--ordered">
              {report.recommendations.slice(0, 5).map((r, i) => (
                <li key={i} className="az-report-list-item">
                  {r}
                </li>
              ))}
            </ol>
          </div>
        ) : null}

        {report.contentStrategy.length > 0 ? (
          <div className="az-report-block" aria-labelledby="az-report-strategy">
            <h4 id="az-report-strategy" className="az-report-block-title">
              <span className="material-symbols-rounded az-report-block-icon" aria-hidden="true">
                strategy
              </span>
              {t('analyzer.reportContentStrategy')}
            </h4>
            <p className="az-report-strategy-text">{report.contentStrategy}</p>
          </div>
        ) : null}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: secao de parecer da IA com botao + estados
// ---------------------------------------------------------------------------

interface AiReportSectionProps {
  readonly reportState: ReportState;
  readonly onGenerate: () => Promise<void>;
  readonly onReset: () => void;
}

function AiReportSection({ reportState, onGenerate, onReset }: AiReportSectionProps): JSX.Element {
  const isGenerating = reportState.kind === 'generating';

  // Ref para rolar para o resultado quando pronto
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (reportState.kind === 'ready' && sectionRef.current !== null) {
      if (typeof sectionRef.current.scrollIntoView === 'function') {
        sectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [reportState.kind]);

  if (reportState.kind === 'ready') {
    return (
      <div ref={sectionRef}>
        <ReportPanel report={reportState.report} />
        <button type="button" className="az-report-regenerate-btn" onClick={onReset}>
          <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
            refresh
          </span>
          {t('analyzer.reportGenerate')}
        </button>
      </div>
    );
  }

  return (
    <section
      className="az-section az-section--report-cta"
      ref={sectionRef}
      aria-labelledby="az-report-cta-heading"
    >
      <h3 id="az-report-cta-heading" className="az-section-title">
        {t('analyzer.reportSection')}
      </h3>

      {reportState.kind === 'generating' ? (
        <div className="az-report-generating" role="status" aria-live="polite">
          <div className="az-report-spinner" aria-hidden="true">
            <span className="material-symbols-rounded az-spinner-icon">progress_activity</span>
          </div>
          <p className="az-report-generating-label">{t('analyzer.reportGenerating')}</p>
          <p className="az-report-generating-hint">{t('analyzer.reportGeneratingHint')}</p>
          <div className="az-report-skeleton-preview" aria-hidden="true">
            <Skeleton variant="heading" width="60%" />
            <Skeleton variant="text" width="100%" />
            <Skeleton variant="text" width="80%" />
            <Skeleton variant="text" width="90%" />
          </div>
        </div>
      ) : null}

      {reportState.kind === 'rate_limited' ? (
        <div className="az-report-state-msg az-report-state-msg--warn" role="alert">
          <span className="material-symbols-rounded az-note-icon" aria-hidden="true">
            schedule
          </span>
          {t('analyzer.reportRateLimit')}
        </div>
      ) : null}

      {reportState.kind === 'error' ? (
        <div className="az-report-state-msg az-report-state-msg--error" role="alert">
          <span className="material-symbols-rounded az-note-icon" aria-hidden="true">
            error
          </span>
          {t('analyzer.reportError')}
        </div>
      ) : null}

      {!isGenerating ? (
        <button
          type="button"
          className="az-report-generate-btn"
          onClick={() => void onGenerate()}
          disabled={isGenerating}
        >
          <span className="material-symbols-rounded" style={{ fontSize: 16 }} aria-hidden="true">
            auto_awesome
          </span>
          {t('analyzer.reportGenerate')}
        </button>
      ) : null}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Sub-componente: seletor de post_limit
// ---------------------------------------------------------------------------

interface PostLimitSelectorProps {
  readonly value: PostLimit;
  readonly onChange: (v: PostLimit) => void;
}

const POST_LIMIT_LABEL_KEYS: Record<PostLimit, string> = {
  10: 'analyzer.postLimit10',
  25: 'analyzer.postLimit25',
  50: 'analyzer.postLimit50',
  100: 'analyzer.postLimit100',
};

function PostLimitSelector({ value, onChange }: PostLimitSelectorProps): JSX.Element {
  const selectId = 'az-post-limit-select';
  return (
    <div className="az-post-limit-row">
      <label htmlFor={selectId} className="az-post-limit-label">
        {t('analyzer.postLimitLabel')}
      </label>
      <select
        id={selectId}
        className="az-post-limit-select"
        value={value}
        aria-label={t('analyzer.postLimitAriaLabel')}
        onChange={(e) => {
          const num = Number(e.target.value) as PostLimit;
          if ((POST_LIMIT_OPTIONS as readonly number[]).includes(num)) {
            onChange(num);
          }
        }}
      >
        {POST_LIMIT_OPTIONS.map((opt) => (
          <option key={opt} value={opt}>
            {t(POST_LIMIT_LABEL_KEYS[opt] as Parameters<typeof t>[0])}
          </option>
        ))}
      </select>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Componente principal: DigitalInstagramTab (com Analyzer)
// ---------------------------------------------------------------------------

export function DigitalInstagramTab(): JSX.Element {
  const [postLimit, setPostLimit] = useState<PostLimit>(DEFAULT_POST_LIMIT);
  const { state: profileState, retry } = useAnalyzerProfile(postLimit);
  const { state: reportState, generate, reset } = useAnalyzerReport(postLimit);

  // --- Estado: loading ---
  if (profileState.kind === 'loading') {
    return (
      <div
        role="tabpanel"
        id="digital-tabpanel-instagram"
        aria-labelledby="digital-tab-instagram"
        className="digital-tabpanel"
      >
        <div className="az-loading-wrapper">
          <SkeletonGrid count={6} />
        </div>
      </div>
    );
  }

  // --- Estado: sem fonte conectada (404 no_instagram_source) ---
  if (profileState.kind === 'no_source') {
    return (
      <div
        role="tabpanel"
        id="digital-tabpanel-instagram"
        aria-labelledby="digital-tab-instagram"
        className="digital-tabpanel"
      >
        <div className="az-empty-state" aria-live="polite">
          <span className="material-symbols-rounded az-empty-icon" aria-hidden="true">
            photo_camera
          </span>
          <p className="az-empty-title">{t('analyzer.errorNoSource')}</p>
          <p className="az-empty-desc">{t('analyzer.errorNoSourceDesc')}</p>
          <Link to="/sources" className="az-empty-cta">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              add_link
            </span>
            {t('analyzer.errorNoSourceCta')}
          </Link>
        </div>
      </div>
    );
  }

  // --- Estado: conta nao conectada (409 source_not_connected) ---
  if (profileState.kind === 'not_connected') {
    return (
      <div
        role="tabpanel"
        id="digital-tabpanel-instagram"
        aria-labelledby="digital-tab-instagram"
        className="digital-tabpanel"
      >
        <div className="az-empty-state" aria-live="polite">
          <span className="material-symbols-rounded az-empty-icon" aria-hidden="true">
            link_off
          </span>
          <p className="az-empty-title">{t('analyzer.errorNotConnected')}</p>
          <p className="az-empty-desc">{t('analyzer.errorNotConnectedDesc')}</p>
          <Link to="/sources" className="az-empty-cta">
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              refresh
            </span>
            {t('analyzer.errorNotConnectedCta')}
          </Link>
        </div>
      </div>
    );
  }

  // --- Estado: erro de coleta (502 ou outro) ---
  if (profileState.kind === 'error') {
    return (
      <div
        role="tabpanel"
        id="digital-tabpanel-instagram"
        aria-labelledby="digital-tab-instagram"
        className="digital-tabpanel"
      >
        <div className="az-error-state" role="alert">
          <p className="az-error-message">{t('analyzer.errorCollect')}</p>
          <button type="button" className="az-retry-btn" onClick={retry}>
            <span className="material-symbols-rounded" style={{ fontSize: 14 }} aria-hidden="true">
              refresh
            </span>
            {t('analyzer.errorRetry')}
          </button>
        </div>
      </div>
    );
  }

  // --- Estado: pronto ---
  const { profile } = profileState;

  return (
    <div
      role="tabpanel"
      id="digital-tabpanel-instagram"
      aria-labelledby="digital-tab-instagram"
      className="digital-tabpanel az-tabpanel"
    >
      {/* Cabecalho da aba: titulo + subtitulo a esquerda, faixa de periodo a direita */}
      <div className="digital-section-header">
        <div className="digital-section-header-text">
          <h2 className="digital-section-title">{t('digital.instagramTitle')}</h2>
          <p className="digital-section-sub">{t('digital.instagramSub')}</p>
        </div>
        <DigitalPeriodBanner />
      </div>

      {/* 0. Seletor de quantidade de posts */}
      <PostLimitSelector value={postLimit} onChange={setPostLimit} />

      {/* 1. Header de perfil */}
      <section className="az-section" aria-labelledby="az-profile-heading">
        <h2 id="az-profile-heading" className="az-section-title">
          {t('analyzer.profileSection')}
        </h2>
        <ProfileHeader profile={profile} />
        {!profile.insightsAvailable ? (
          <div className="az-note az-note--info" role="note">
            <span className="material-symbols-rounded az-note-icon" aria-hidden="true">
              info
            </span>
            {t('analyzer.insightsUnavailableNote')}
          </div>
        ) : null}
        <ProfileKpiGrid profile={profile} />
      </section>

      {/* 2. Hashtags + distribuicao de conteudo + desempenho por formato */}
      <TopHashtags tags={profile.topHashtags} />
      <ContentDistribution dist={profile.contentDistribution} />
      <FormatPerformanceTable
        formats={profile.formatPerformance}
        bestFormat={profile.bestFormat}
        insightsAvailable={profile.insightsAvailable}
      />

      {/* 3. Posts recentes */}
      <RecentPosts posts={profile.recentPosts} insightsAvailable={profile.insightsAvailable} />

      {/* 4. Parecer da IA */}
      <AiReportSection reportState={reportState} onGenerate={generate} onReset={reset} />
    </div>
  );
}
